import "core-js/stable";
import "regenerator-runtime/runtime";
import Vue from "vue";
import VueCompositionAPI from "@vue/composition-api";
import App from "./App.vue";
import router from "./router";
import store from "./store/store";
import "./custom.scss";
import i18next from "i18next";
import I18NextVue from "i18next-vue";
import AlertWindowMaker from "./custom-plugins/AlertWindowMaker.js";
import Seo from "./custom-plugins/Seo.js";
import CurrentDate from "./custom-plugins/currentDate.js";
import FormatNumber from "./custom-plugins/formatNumber.js";
import ErrorMessage from "./custom-plugins/errorMessage.js";
import ShortenString from "./custom-plugins/shortenString.js";
import SoundSystem from "./custom-plugins/SoundSystem.js";
import VueCookies from "vue-cookies";
import Firebase from "./custom-plugins/Firebase.js";
import GoogleTag from "./custom-plugins/GoogleTagManager.js";
import FeatureUnlockedChecker from "./custom-plugins/featureUnlockedChecker.js";
import VueMeta from "vue-meta";
import whitelabelConfig from "./whitelabel-config.js";

import axios from "axios";
// locale files
import translationEN from "./locales/en.json";
import translationDE from "./locales/de.json";

import BasePopover from "./components/wrappers/base-popover.vue";
Vue.component("BPopover", BasePopover);

import BaseModal from "./components/wrappers/base-modal.vue";
Vue.use({
  install(Vue) {
    Vue.mixin({
      created() {
        // ensure each root adds the bus handlers just once
        if (this === this.$root) {
          const root = this;
          const mounted = new Set();
          const pendingShow = new Set();
          const pendingHide = new Set();
          root._modalBus = {
            show(id) {
              if (mounted.has(id)) root.$emit("base-modal:show", id);
              else pendingShow.add(id);
            },
            hide(id) {
              if (mounted.has(id)) root.$emit("base-modal:hide", id);
              else pendingHide.add(id);
            },
            _register(id) {
              if (!id) return;
              mounted.add(id);
              // Defer flush so a pending show() doesn't fire synchronously
              // during the registering component's `mounted()` hook — gives
              // Bootstrap Modal a clean tick to finish wiring before show.
              Vue.nextTick(() => {
                if (pendingShow.has(id)) {
                  pendingShow.delete(id);
                  root.$emit("base-modal:show", id);
                }
                if (pendingHide.has(id)) {
                  pendingHide.delete(id);
                  root.$emit("base-modal:hide", id);
                }
              });
            },
            _unregister(id) {
              if (!id) return;
              mounted.delete(id);
            },
          };
        }
      },
    });

    Object.defineProperty(Vue.prototype, "$bvModal", {
      get() {
        // on any component, this.$bvModal.show('modal-shop') will emit on root
        return this.$root._modalBus;
      },
    });
  },
});

Vue.component("BModal", BaseModal);
// Composition API
Vue.use(VueCompositionAPI);

import BaseForm from "./components/wrappers/base-form.vue";
import BaseTable from "./components/wrappers/base-table.vue";
import BasePagination from "./components/wrappers/base-pagination.vue";
import BaseAlert from "./components/wrappers/base-alert.vue";
import BaseSpinner from "./components/wrappers/base-spinner.vue";
import BaseCarousel from "./components/wrappers/base-carousel.vue";
import BaseCarouselSlide from "./components/wrappers/base-carousel-slide.vue";
import BaseButton from "./components/wrappers/base-button.vue";
import BaseInputGroup from "./components/wrappers/base-input-group.vue";
import BaseInputGroupAppend from "./components/wrappers/base-input-group-append.vue";
import BaseRow from "./components/wrappers/base-row.vue";
import BaseCol from "./components/wrappers/base-col.vue";
import BaseNav from "./components/wrappers/base-nav.vue";
import BaseNavbar from "./components/wrappers/base-navbar.vue";
import BaseNavItem from "./components/wrappers/base-nav-item.vue";
import BaseNavbarNav from "./components/wrappers/base-navbar-nav.vue";

Vue.component("BRow", BaseRow);
Vue.component("BCol", BaseCol);
Vue.component("BNavbar", BaseNavbar);
Vue.component("BNav", BaseNav);
Vue.component("BNavItem", BaseNavItem);
Vue.component("BNavbarNav", BaseNavbarNav);
Vue.component("BButton", BaseButton);
Vue.component("BInputGroup", BaseInputGroup);
Vue.component("BInputGroupAppend", BaseInputGroupAppend);
Vue.component("BForm", BaseForm);
Vue.component("BTable", BaseTable);
Vue.component("BPagination", BasePagination);
Vue.component("BAlert", BaseAlert);
Vue.component("BSpinner", BaseSpinner);
Vue.component("BCarousel", BaseCarousel);
Vue.component("BCarouselSlide", BaseCarouselSlide);
const bvModalShim = {
  show(id) {
    let tries = 10;
    const delay = 100;

    const tryShow = () => {
      const el = document.getElementById(id);
      if (!el && --tries > 0) return setTimeout(tryShow, delay);
      if (!el) return console.warn(`No BaseModal with id="${id}" found!`);

      let vm =
        el.__vue__ ||
        (el.__vueParentComponent && el.__vueParentComponent.proxy);
      let walked = false;

      while (vm && typeof vm.show !== "function") {
        vm = vm.$parent;
        walked = true;
      }

      if (vm && typeof vm.show === "function") {
        vm.show();
      } else if (--tries > 0) {
        setTimeout(tryShow, delay);
      } else {
        const msg = walked
          ? `BaseModal with id="${id}" found but no show() method.`
          : `Vue instance not found for BaseModal with id="${id}".`;
        console.warn(msg);
      }
    };

    tryShow();
  },
  hide(id) {
    const el = document.getElementById(id);
    if (!el) return;
    let vm =
      el.__vue__ || (el.__vueParentComponent && el.__vueParentComponent.proxy);
    while (vm && typeof vm.hide !== "function") vm = vm.$parent;
    vm && vm.hide();
  },
};

// Vue.use({
//   install(Vue) {
//     Object.defineProperty(Vue.prototype, "$bvModal", {
//       get: () => bvModalShim,
//     });
//   },
// });
// Vue.prototype.$bvModal = Vue.prototype.$modal;

// GSAP
import gsap from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
gsap.registerPlugin(MotionPathPlugin);
Vue.mixin({
  created() {
    this.gsap = gsap;
  },
});
Vue.prototype.$eventBus = new Vue();
Vue.prototype.$http = axios;
Vue.prototype.$whitelabel = whitelabelConfig;
axios.interceptors.response.use(
  (response) => {
    if (response.status === 200 || response.status === 201) {
      if (store.getters.isMaintenanceModeEnabled) {
        store.commit("disableMaintenanceMode");
      }
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  },
  (error) => {
    if (!error.response) {
      return Promise.resolve(error);
    }

    if (error.response.status) {
      switch (error.response.status) {
        // force logout if unauthenticated
        case 401:
          store.dispatch("destroyStore").then(router.push("/"));
          break;
        case 423:
          store.dispatch("handleTermsNotAccepted", error.response);

        case 503:
          console.log("maintenance mode?!");
          // store.commit("enableMaintenanceMode");
          // router.push("/maintenance");
          break;
        default:
          console.log(error);
          break;
      }
      return Promise.reject(error.response);
    }
  }
);

Vue.config.performance = true;
Vue.use(VueCookies);
Vue.use(AlertWindowMaker);
Vue.use(Seo);
Vue.use(CurrentDate);
Vue.use(FormatNumber);
Vue.use(ErrorMessage);
Vue.use(ShortenString);
Vue.use(SoundSystem);
Vue.use(Firebase);
Vue.use(GoogleTag);
Vue.use(FeatureUnlockedChecker);
Vue.use(VueMeta);

i18next.init({
  lng: store.state.locale,
  resources: {},
  saveMissing: true,
  missingKeyHandler: (lng, ns, key) => {
    console.warn(`Missing translation for key: ${key} in language: ${lng}`);
  },
  interpolation: {
    prefix: "{",
    suffix: "}",
  },
});
i18next.addResourceBundle("en", "translation", translationEN, true);
i18next.addResourceBundle("de", "translation", translationDE, true);
Vue.use(I18NextVue, { i18next });

Vue.config.productionTip = false;
if (process.env.NODE_ENV === "production") {
  console.log = function () {}; // Disable all console logs in production environment
}

const sessionToken = sessionStorage.getItem("auth_token");
const localToken = localStorage.getItem("auth_token");
if (sessionToken) {
  Vue.prototype.$http.defaults.headers.common["Authorization"] = sessionToken;
} else if (localToken) {
  Vue.prototype.$http.defaults.headers.common["Authorization"] = localToken;
}

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount("#app");
